import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { toggleLoading } from "../../../redux";
import {Store} from "@ngrx/store";
import {ActivatedRoute, Router} from "@angular/router";
import {MandateService} from "../../services";
import {AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn} from "@angular/forms";
import {COMMA, ENTER} from "@angular/cdk/keycodes";
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";
import {ToastrService} from "ngx-toastr";
import {iMandateRequest, iMandateResponse} from "../../models";
import {UtilService} from "../../../shared/services";
import { LookupService, NotesService } from 'src/app/services';
import { DataService } from 'src/app/services/data/data.service';

@Component({
  selector: 'app-mandate-info',
  templateUrl: './mandate-info.component.html',
  styleUrls: ['./mandate-info.component.scss']
})
export class MandateInfoComponent implements OnInit {

  userForm = new FormGroup({
    id: new FormControl(),
    countryId: new FormControl(''),
    stateOrProvinceId: new FormControl(''),
    city: new FormControl(''),
    firmAum: new FormControl(0),
    programAum: new FormControl(0),
    targetNetMoic: new FormControl(0),
    targetNetIrr: new FormControl(0),
    minimumDuration: new FormControl(0),
    maximumDuration: new FormControl(0),
    minTicketSize: new FormControl(0),
    maxTicketSize: new FormControl(0),
    avgTicketSize: new FormControl(0),
    investorMandateDeals: new FormArray<FormControl>([], atLeastOneCheckboxCheckedValidator()),
    investorMandateAssets: new FormArray<FormControl>([], atLeastOneCheckboxCheckedValidator()),
    investorMandateAreasOfFocus: new FormArray<FormControl>([]),
    investorMandateAnchorInterests: new FormArray<FormControl>([]),
    investorMandateTimeLines: new FormControl(),
    notes: new FormControl(''),
    workflowStatus: new FormControl({value: '', disabled: !this.util.isAdmin()})
  });

  //theme input
  @ViewChild('themeInput') themeInput!: ElementRef<HTMLInputElement>;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  selectedThemes: string[] = [];
  chipInputCtrl = new FormControl('');

  constructor(
    private store: Store,
    private activatedRoute: ActivatedRoute,
    private mandateService: MandateService,
    private toastr: ToastrService,
    public util: UtilService,
    public lookupService: LookupService,
    public notesService: NotesService
  ) {
  }

  ngOnInit() {
    var mandateId = this.activatedRoute.snapshot.parent?.paramMap.get("id") || "";
    // console.log("mandate id", this.mandateId);

    this.mandateService.getMandate(mandateId).subscribe({
      next: (mandate: iMandateResponse) => {
        // console.log("mandate", mandate);
        this.patchValues(mandate);
      }
    })
  }

  private patchValues(mandate: iMandateResponse) {
    this.userForm.patchValue(mandate);

    // deal types
    this.lookupService.lookups.dealTypes.forEach(x => {
      var isChecked = mandate.investorMandateDeals.map(x => x.key).includes(x.key);
      this.dealTypes.push(new FormControl(isChecked));
    })

    // asset classes
    this.lookupService.lookups.assetTypes.forEach(x => {
      var isChecked : boolean = mandate.investorMandateAssets.map(x => x.key).includes(x.key);
      this.assetClasses.push(new FormControl(isChecked));
    })

    // anchor Types
    this.lookupService.lookups.anchorInterestTypes.forEach(x => {
      var isChecked = mandate.investorMandateAnchorInterests.map(x => x.key).includes(x.key);
      this.anchorInterestTypes.push(new FormControl(isChecked));
    })

    // timelines
    if (mandate.investorMandateTimeLines.length > 0) {
      this.userForm.patchValue({ investorMandateTimeLines : mandate.investorMandateTimeLines[0].key });
    }

    // themes / keywords
    this.selectedThemes = mandate.investorMandateAreasOfFocus.map(x => x.key);
  }

  get dealTypes(): FormArray { return this.userForm.get("investorMandateDeals") as FormArray; }
  get assetClasses(): FormArray { return this.userForm.get("investorMandateAssets") as FormArray; }
  get anchorInterestTypes(): FormArray { return this.userForm.get("investorMandateAnchorInterests") as FormArray; }

  // themes / keywords
  addWord(event: any) {
    var optionValue = event.value;
    if (this.selectedThemes.includes(optionValue) == false) {
      // get the value of the key 
      if (this.selectedThemes.includes(optionValue) == false) {
        this.selectedThemes.push(optionValue);
      }
      event.chipInput!.clear();
    }
  }

  removeWork(word: string) {
    this.selectedThemes = this.selectedThemes.filter(x => x != word);
  }

  selectedTheme(event: MatAutocompleteSelectedEvent) {
    var optionValue = event.option.value;
    var values = this.lookupService.lookups.areasOfFocus.filter(x => x.key == optionValue);
    if (values.length > 0) {
      if (this.selectedThemes.includes(values[0].value) == false) {
        this.selectedThemes.push(values[0].value);
      }
    }
  }
  save() {
    var investorMandateDeals : {key: string}[] = [];
    var investorMandateAssets : {key: string}[] = [];
    var investorMandateAnchorInterests : {key: string}[] = [];
    
    this.userForm.value.investorMandateDeals?.forEach((x, i) => {
      if (x === true) {
        investorMandateDeals.push({ key : this.lookupService.lookups.dealTypes[i].key });
      }
    });

    this.userForm.value.investorMandateAssets?.forEach((x, i) => {
      if (x === true) {
        investorMandateAssets.push({ key : this.lookupService.lookups.assetTypes[i].key });
      }
    });

    this.userForm.value.investorMandateAnchorInterests?.forEach((x, i) => {
      if (x === true) {
        investorMandateAnchorInterests.push({ key : this.lookupService.lookups.anchorInterestTypes[i].key });
      }
    });

    var payload = this.userForm.value;
    payload.investorMandateDeals = investorMandateDeals;
    payload.investorMandateAssets = investorMandateAssets;
    payload.investorMandateAnchorInterests = investorMandateAnchorInterests;

    //format the timelines
    payload.investorMandateTimeLines = [
      {
        key: this.userForm.get("investorMandateTimeLines")?.value || '',
      }
    ];

    // areas of focus
    payload.investorMandateAreasOfFocus = this.selectedThemes.map(x =>
    {
      return { key: x }
    });

    console.log("Save mandate formatted", payload);
    
    this.store.dispatch(toggleLoading({loading: true}));
    this.mandateService.updateMandate(payload).subscribe({
      next: res => {
        this.store.dispatch(toggleLoading({loading: false}));
        this.toastr.success("Mandate Updated!", $localize`:@@companyName:Rondeivu`);
      }, error: error => {
        this.toastr.error("Unable to update mandate!", $localize`:@@companyName:Rondeivu`);
        this.store.dispatch(toggleLoading({loading: false}));
      }
    });
  }
}

export function atLeastOneCheckboxCheckedValidator(): ValidatorFn {
  return (formArray: AbstractControl): ValidationErrors | null => {
    if (!formArray || !formArray.value || !Array.isArray(formArray.value)) {
      return null;
    }

    const totalChecked = formArray.value.filter((v: boolean) => v === true).length;
    return totalChecked >= 1 ? null : { minChecked: true };
  }
}